import { useEffect, useState, useCallback, useRef } from "react";
import { X, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

/**
 * OnboardingTour — Tour guidé contextuel léger (sans dépendance externe).
 *
 * Affiche un overlay assombri avec un "spotlight" autour de l'élément ciblé
 * et une popover explicative. L'utilisateur navigue avec Suivant/Précédent.
 *
 * Props :
 *   steps        : [{ selector: 'data-testid attribute', title, description, position }]
 *   storageKey   : clé localStorage pour mémoriser que l'utilisateur a vu/skip le tour
 *   autoStart    : démarrer automatiquement si pas vu
 *   onComplete   : callback quand on finit
 */

const POSITION_CLASSES = {
  bottom: "top-full mt-3 left-1/2 -translate-x-1/2",
  top: "bottom-full mb-3 left-1/2 -translate-x-1/2",
  right: "left-full ml-3 top-1/2 -translate-y-1/2",
  left: "right-full mr-3 top-1/2 -translate-y-1/2",
};

export default function OnboardingTour({
  steps = [],
  storageKey = "blitzz_onboarding_done",
  autoStart = true,
  onComplete,
  forceShow = false,
}) {
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const overlayRef = useRef(null);

  const currentStep = steps[stepIndex];

  // Determine if we should show
  useEffect(() => {
    if (forceShow) {
      setActive(true);
      setStepIndex(0);
      return;
    }
    if (!autoStart) return;
    try {
      const seen = localStorage.getItem(storageKey);
      if (!seen) setActive(true);
    } catch {}
  }, [autoStart, storageKey, forceShow]);

  // Locate the target element + compute spotlight rect
  const updateRect = useCallback(() => {
    if (!currentStep) return;
    const sel = currentStep.selector?.startsWith("[")
      ? currentStep.selector
      : `[data-testid="${currentStep.selector}"]`;
    const el = document.querySelector(sel);
    if (!el || el.offsetParent === null) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    // Element hidden, collapsed or off-screen (e.g. sidebar drawer on mobile)
    if (r.width < 2 || r.height < 2 || r.right < 0 || r.left > window.innerWidth) {
      setRect(null);
      return;
    }
    setRect({
      top: r.top + window.scrollY,
      left: r.left + window.scrollX,
      width: r.width,
      height: r.height,
    });
    // Scroll into view if needed
    if (r.top < 0 || r.bottom > window.innerHeight) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [currentStep]);

  useEffect(() => {
    if (!active) return;
    updateRect();
    const onResize = () => updateRect();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [active, stepIndex, updateRect]);

  // Compute popover position next to spotlight
  useEffect(() => {
    const CARD_H = 300; // estimated popover height incl. Safari bottom bar margin
    const isMobile = window.innerWidth < 768;
    if (!rect) {
      // Center of viewport fallback (slightly above center on mobile to stay clickable)
      setPopoverPos({
        top: Math.max(20, window.innerHeight * (isMobile ? 0.3 : 0.5) - 100),
        left: Math.max(16, window.innerWidth / 2 - (isMobile ? window.innerWidth * 0.46 : 200)),
      });
      return;
    }
    const pos = currentStep?.position || "bottom";
    let top = rect.top + rect.height + 12;
    let left = rect.left + rect.width / 2 - 200;
    if (pos === "top") {
      top = rect.top - 200;
      left = rect.left + rect.width / 2 - 200;
    } else if (pos === "right") {
      top = rect.top + rect.height / 2 - 80;
      left = rect.left + rect.width + 12;
    } else if (pos === "left") {
      top = rect.top + rect.height / 2 - 80;
      left = rect.left - 412;
    }
    // Clamp to visible viewport (keep buttons above iOS Safari bottom bar)
    top = Math.max(20 + window.scrollY, Math.min(window.innerHeight + window.scrollY - CARD_H, top));
    left = Math.max(16, Math.min(window.innerWidth - (isMobile ? window.innerWidth * 0.94 : 420), left));
    setPopoverPos({ top, left });
  }, [rect, currentStep]);

  const finish = useCallback(
    (skipped = false) => {
      try {
        localStorage.setItem(storageKey, skipped ? "skipped" : "done");
      } catch {}
      setActive(false);
      onComplete?.(!skipped);
    },
    [storageKey, onComplete],
  );

  const next = () => {
    if (stepIndex < steps.length - 1) setStepIndex((i) => i + 1);
    else finish(false);
  };
  const prev = () => stepIndex > 0 && setStepIndex((i) => i - 1);

  if (!active || !steps.length) return null;

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none" data-testid="onboarding-tour" ref={overlayRef}>
      {/* Backdrop with spotlight cutout via SVG mask — click closes the tour so the app is never blocked */}
      <svg className="absolute inset-0 w-full h-full pointer-events-auto" onClick={() => finish(true)} data-testid="onboarding-backdrop">
        <defs>
          <mask id="onboarding-spot">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left - 8}
                y={rect.top - 8 - window.scrollY}
                width={rect.width + 16}
                height={rect.height + 16}
                rx={10}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(8,5,18,0.78)" mask="url(#onboarding-spot)" />
      </svg>

      {/* Spotlight glowing border */}
      {rect && (
        <div
          className="absolute pointer-events-none rounded-xl ring-2 ring-[#f7c935] shadow-[0_0_40px_rgba(247,201,53,0.45)] animate-pulse"
          style={{
            top: rect.top - 8 - window.scrollY,
            left: rect.left - 8,
            width: rect.width + 16,
            height: rect.height + 16,
          }}
        />
      )}

      {/* Popover */}
      <div
        className="absolute w-[400px] max-w-[92vw] bg-[#14101c] border border-[#f7c935]/30 rounded-2xl shadow-2xl shadow-[#f7c935]/10 p-5 pointer-events-auto"
        style={{ top: popoverPos.top - window.scrollY, left: popoverPos.left }}
        data-testid="onboarding-popover"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#f7c935] text-[#14101c] flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Étape {stepIndex + 1} / {steps.length}
            </div>
          </div>
          <button
            onClick={() => finish(true)}
            className="text-slate-500 hover:text-white transition-colors"
            aria-label="Fermer le tutoriel"
            data-testid="onboarding-skip"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <h3 className="text-base font-bold text-white mt-3">{currentStep?.title}</h3>
        <p className="text-sm text-slate-300 mt-1.5 leading-relaxed">{currentStep?.description}</p>

        {/* Progress bar */}
        <div className="mt-4 h-1 bg-white/5 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#f7c935] to-[#f5b800] transition-all"
            style={{ width: `${((stepIndex + 1) / steps.length) * 100}%` }}
          />
        </div>

        <div className="mt-4 flex items-center justify-between">
          <button
            onClick={prev}
            disabled={stepIndex === 0}
            className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="onboarding-prev"
          >
            <ArrowLeft className="w-3 h-3" /> Précédent
          </button>
          <button
            onClick={() => finish(true)}
            className="text-[11px] text-slate-500 hover:text-slate-300"
            data-testid="onboarding-skip-btn"
          >
            Passer le tutoriel
          </button>
          <button
            onClick={next}
            className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b800] font-bold text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1"
            data-testid="onboarding-next"
          >
            {stepIndex === steps.length - 1 ? "Terminer" : "Suivant"} <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
